import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import type { CallState } from '@comms/shared';
import { displayName, useStore } from '../store';
import { startRinging, stopRinging } from '../lib/sounds';
import {
  declineCall,
  joinCall,
  clearVoiceError,
  leaveVoice,
  toggleCamera,
  toggleDeafen,
  toggleMute,
  useVoice,
} from '../lib/voice';
import { useNativeAudio } from '../lib/native';
import { dmTitle } from './DmSidebar';
import { AudioOutputButton, CallButton, CallWarnings } from './VoiceUi';
import { VideoStage, useHasVideo } from './VideoStage';
import { ScreenShareButton } from './GoLive';
import { CallVolume } from './CallVolume';
import { Avatar, Button, Icon, cx } from './ui';

/** Call stage shown at the top of a DM while a call is going on there. */
export function DmCallPanel({ channelId }: { channelId: string }) {
  const call = useStore((s) => s.calls[channelId]);
  const voice = useVoice();
  const enabled = useStore((s) => s.voiceEnabled);
  const mediaOnly = useNativeAudio((s) => s.mediaOnly);
  const here = voice.kind === 'dm' && voice.channelId === channelId && voice.status !== 'idle';
  const hasVideo = useHasVideo(null) && here;

  if (!call && !here) return null;
  const participants = call?.participants ?? [];
  const ringing = call?.ringing ?? [];

  return (
    <section
      aria-label="Call"
      data-encrypted={here && voice.encrypted ? 'true' : 'false'}
      className={cx(
        'flex shrink-0 flex-col gap-3 bg-black/90 p-4',
        hasVideo || voice.screen ? 'h-[55%]' : '',
      )}
    >
      <div className="flex items-center justify-center gap-2 text-xs text-text-3">
        <Icon
          name="lock"
          className={cx('h-3 w-3', here && voice.encrypted ? 'text-success' : '')}
        />
        {here
          ? voice.status === 'connected'
            ? voice.encrypted
              ? 'End-to-end encrypted call'
              : 'Securing call…'
            : 'Connecting…'
          : 'Call in progress'}
      </div>
      {here && <CallWarnings />}
      {here && voice.error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded bg-danger/20 px-2 py-1 text-xs text-text-0"
        >
          <span className="flex-1">{voice.error}</span>
          <button type="button" onClick={clearVoiceError} aria-label="Dismiss">
            <Icon name="x" className="h-3 w-3" />
          </button>
        </div>
      )}
      {here && mediaOnly && (
        <p role="status" data-media-only className="text-center text-xs text-warning">
          This phone is playing the call as media audio, so it can only use the loudspeaker.
        </p>
      )}
      {here && <VideoStage guildId={null} />}
      <div className="flex shrink-0 flex-wrap justify-center gap-4">
        {participants.map((id) => (
          <div key={id} className="flex flex-col items-center gap-1">
            <span
              className={cx(
                'rounded-full',
                here && voice.speaking.includes(id) && 'ring-2 ring-success',
              )}
            >
              <Avatar userId={id} size={hasVideo ? 36 : 64} />
            </span>
            <span className="text-xs text-text-1">{displayName(useStore.getState(), id)}</span>
          </div>
        ))}
        {ringing.map((id) => (
          <div key={id} className="flex flex-col items-center gap-1 opacity-50" title="Ringing…">
            <span className="animate-pulse rounded-full">
              <Avatar userId={id} size={hasVideo ? 36 : 64} />
            </span>
            <span className="text-xs text-text-3">Ringing…</span>
          </div>
        ))}
      </div>
      <div className="flex shrink-0 justify-center gap-3">
        {!here ? (
          enabled && (
            <>
              <Button
                className="bg-success hover:bg-success"
                onClick={() => void joinCall(channelId)}
              >
                Join call
              </Button>
              <Button variant="secondary" onClick={() => void joinCall(channelId, { video: true })}>
                Join with video
              </Button>
            </>
          )
        ) : (
          <>
            <CallButton
              title={voice.muted ? 'Unmute' : 'Mute'}
              active={voice.muted}
              icon={voice.muted ? 'micOff' : 'mic'}
              onClick={toggleMute}
            />
            <CallButton
              title={voice.deafened ? 'Undeafen' : 'Deafen'}
              active={voice.deafened}
              icon={voice.deafened ? 'headphonesOff' : 'headphones'}
              onClick={toggleDeafen}
            />
            <CallButton
              title={voice.camera ? 'Turn off camera' : 'Turn on camera'}
              on={voice.camera}
              icon="video"
              onClick={toggleCamera}
            />
            <ScreenShareButton />
            <AudioOutputButton />
            <button
              type="button"
              title="Leave call"
              aria-label="Leave call"
              onClick={() => void leaveVoice()}
              className="rounded-full bg-danger p-3 text-white hover:brightness-110"
            >
              <Icon name="phoneOff" />
            </button>
          </>
        )}
      </div>
      {here && <CallVolume className="mx-auto w-full max-w-xs shrink-0" />}
    </section>
  );
}

/** Ring while an incoming call is waiting (unless call sounds are off or you're on DND). */
function useRingtone(active: boolean) {
  useEffect(() => {
    if (!active) return;
    startRinging();
    return stopRinging;
  }, [active]);
}

/** Incoming-call prompts, shown anywhere in the app. */
export function IncomingCalls() {
  const meId = useStore((s) => s.me?.id);
  const inCall = useVoice((s) => (s.kind === 'dm' ? s.channelId : null));
  const calls = useStore(
    useShallow((s) =>
      Object.values(s.calls).filter(
        (c) => meId && c.ringing.includes(meId) && c.channelId !== inCall,
      ),
    ),
  );
  useRingtone(calls.length > 0);
  if (calls.length === 0) return null;
  return (
    <div className="fixed bottom-6 right-6 z-40 space-y-3">
      {calls.map((c) => (
        <IncomingCallCard key={c.channelId} call={c} />
      ))}
    </div>
  );
}

function IncomingCallCard({ call }: { call: CallState }) {
  const caller = useStore((s) => displayName(s, call.startedBy));
  const title = useStore((s) =>
    s.channels[call.channelId] ? dmTitle(s, s.channels[call.channelId]!) : caller,
  );
  const isGroup = useStore((s) => (s.channels[call.channelId]?.recipients?.length ?? 2) > 2);
  const navigate = useNavigate();
  const accept = (video: boolean) => {
    navigate(`/channels/@me/${call.channelId}`);
    void joinCall(call.channelId, { video });
  };
  return (
    <div
      role="alertdialog"
      aria-label={`Incoming call from ${caller}`}
      className="w-72 rounded-lg bg-bg-0 p-4 shadow-2xl ring-1 ring-bg-3"
    >
      <div className="flex items-center gap-3">
        <span className="animate-pulse rounded-full ring-2 ring-success">
          <Avatar userId={call.startedBy} size={48} />
        </span>
        <div className="min-w-0">
          <div className="truncate font-semibold text-text-0">{isGroup ? title : caller}</div>
          <div className="flex items-center gap-1 text-xs text-text-3">
            <Icon name="lock" className="h-3 w-3" /> Incoming encrypted call
            {isGroup ? ` from ${caller}` : ''}
          </div>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <Button className="flex-1 bg-success py-1.5 hover:bg-success" onClick={() => accept(false)}>
          Accept
        </Button>
        <Button
          variant="secondary"
          className="py-1.5"
          title="Accept with video"
          onClick={() => accept(true)}
        >
          <Icon name="video" className="h-4 w-4" />
        </Button>
        <Button
          variant="danger"
          className="py-1.5"
          onClick={() => void declineCall(call.channelId)}
        >
          Decline
        </Button>
      </div>
    </div>
  );
}
